/**
 * Hand a URL to the user's own device — the native share sheet where there is one, the clipboard where
 * there is not. Used wherever the product needs to say "keep this and open it somewhere else" without
 * asking for an email address or standing up a backend to send one.
 *
 * `navigator.share` only exists in a secure context on (mostly) mobile browsers, and it REJECTS with an
 * AbortError when the user closes the sheet. A cancel is a decision, not a failure, so it must not fall
 * through to a clipboard write the user never asked for — hence the distinct 'cancelled' outcome.
 */
export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed';

export async function shareOrCopyLink(
  url: string,
  details: { title?: string; text?: string } = {},
): Promise<ShareOutcome> {
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ ...details, url });
      return 'shared';
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return 'cancelled';
      // Any other share failure (unsupported payload, permission) falls through to the clipboard.
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    // No clipboard either (blocked permission, insecure origin) — the caller shows the raw link instead.
    return 'failed';
  }
}
